import React, {useState, useEffect, useRef} from "react";
import {Alert, Button, Card, Progress, Space, Table, Tag, Tabs, Popconfirm, Collapse, notification, Row, Col, Statistic} from "antd";
import {
    PauseOutlined,
    PlayCircleOutlined,
    CloseCircleOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    CloudUploadOutlined,
    ExclamationCircleOutlined,
    WarningOutlined,
    ReloadOutlined
} from "@ant-design/icons";
import {useTranslation} from "react-i18next";
import api from "../api";
import {useUploadsState, useUploadsDispatch} from "../../contexts/UploadsContext";
import {useAssetGroupDispatch} from "../../contexts/AssetGroupContext";
import {useEditAssetsState} from "../../contexts/EditAssetsContext";

const { TabPane } = Tabs;

export default ({upload, onUpdate, showStatusFilter = false})=> {
    const {t} = useTranslation();
    const {uploadState, currentUploadSession} = useUploadsState();
    const uploadsDispatch = useUploadsDispatch();
    const assetGroupDispatch = useAssetGroupDispatch();
    const {assetEdits} = useEditAssetsState();

    const [loading, setLoading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState(upload);
    const [activeTab, setActiveTab] = useState('all');
    const prevUploadState = useRef({stalled_count: 0, errored_count: 0, upload_state: null});
    const pausedNotificationKey = useRef(null);

    useEffect(() => {
        if (upload) {
            setUploadStatus(upload);
            uploadsDispatch({type: 'setUploadSession', session: upload});
            uploadsDispatch({type: 'setUploadState', uploadState: upload.upload_state});
        }
    }, [upload]);

    // Poll for updates while assets are processing
    useEffect(() => {
        if (!upload?.guid) return;

        const needsPolling = uploadStatus && (
            uploadStatus.processing_count > 0 ||
            uploadStatus.uploading_count > 0
        );

        if (!needsPolling) return;

        const pollInterval = setInterval(() => {
            api.get(`/api/uploads/${upload.guid}`).then(res => {
                setUploadStatus(res.data);
                uploadsDispatch({type: 'setUploadSession', session: res.data});
                onUpdate && onUpdate(res.data);
            }).catch(err => {
                console.error('Failed to poll upload status:', err);
            });
        }, 3000); // Poll every 3 seconds while processing

        return () => clearInterval(pollInterval);
    }, [upload?.guid, uploadStatus?.processing_count, uploadStatus?.uploading_count]);

    // Refresh upload stats when assets are edited/deleted (e.g., trashed)
    useEffect(() => {
        if (!upload?.guid) return;

        api.get(`/api/uploads/${upload.guid}`).then(res => {
            setUploadStatus(res.data);
            uploadsDispatch({type: 'setUploadSession', session: res.data});
            onUpdate && onUpdate(res.data);
        }).catch(err => {
            console.error('Failed to refresh upload status after asset edit:', err);
        });
    }, [assetEdits]);

    // Notification for stalled uploads and errored files
    useEffect(() => {
        if (!uploadStatus) return;

        const wasStalled = prevUploadState.current.upload_state === 'stalled' || prevUploadState.current.stalled_count > 0;
        const isNowStalled = uploadStatus.upload_state === 'stalled' || uploadStatus.stalled_count > 0;
        const prevErroredCount = prevUploadState.current.errored_count || 0;
        const currentErroredCount = uploadStatus.errored_count || 0;

        // Show notification when upload becomes stalled
        if (!wasStalled && isNowStalled) {
            notification.error({
                message: t('upload-stalled', 'Upload Stalled'),
                description: t('upload-stalled-description', 'Some files have stopped uploading. Please check your connection and resume the upload.'),
                duration: 0, // Don't auto-close
            });
        }

        // Show notification when files error
        if (currentErroredCount > prevErroredCount) {
            const newErrors = currentErroredCount - prevErroredCount;
            notification.error({
                message: t('files-failed', 'Files Failed to Process'),
                description: t('files-failed-description', '{{count}} file(s) failed to process. Please review the errors below.', {count: newErrors}),
                duration: 0, // Don't auto-close
            });
        }

        // Update previous state
        prevUploadState.current = {
            stalled_count: uploadStatus.stalled_count || 0,
            errored_count: uploadStatus.errored_count || 0,
            upload_state: uploadStatus.upload_state
        };
    }, [uploadStatus?.stalled_count, uploadStatus?.errored_count, uploadStatus?.upload_state]);

    const handlePause = async () => {
        setLoading(true);
        try {
            const res = await api.post(`/api/uploads/${upload.id}/pause`);
            // Merge response with existing upload to preserve relationships like contribution
            const updatedUpload = {...upload, ...res.data};
            setUploadStatus(updatedUpload);
            uploadsDispatch({type: 'setUploadState', uploadState: updatedUpload.upload_state});
            onUpdate && onUpdate(updatedUpload);

            if(updatedUpload.upload_state === 'paused') {
                // Show persistent notification while paused
                const key = `upload-paused-${upload.id}`;
                pausedNotificationKey.current = key;
                notification.warning({
                    key,
                    message: t('upload-paused', 'Upload Paused'),
                    description: t('upload-paused-description-processing', 'The upload has been paused. Files that have already been uploaded will continue to process. Click Resume to continue uploading.'),
                    duration: 0, // Don't auto-dismiss
                });
            }
        } catch (error) {
            console.error('Failed to pause upload:', error);
            notification.error({
                message: t('pause-failed', 'Pause Failed'),
                description: t('pause-failed-description', 'Failed to pause the upload. Please try again.'),
            });
        } finally {
            setLoading(false);
        }
    };

    const handleResume = async () => {
        setLoading(true);
        try {
            const res = await api.post(`/api/uploads/${upload.id}/resume`);
            // Merge response with existing upload to preserve relationships like contribution
            const updatedUpload = {...upload, ...res.data};
            setUploadStatus(updatedUpload);
            uploadsDispatch({type: 'setUploadState', uploadState: updatedUpload.upload_state});
            if(updatedUpload.upload_state === 'active') uploadsDispatch({type: 'clearStalledAssets'});
            onUpdate && onUpdate(updatedUpload);

            // Close the paused notification if it exists
            if (updatedUpload.upload_state === 'active' && pausedNotificationKey.current) {
                notification.close(pausedNotificationKey.current);
                pausedNotificationKey.current = null;
            }
        } catch (error) {
            console.error('Failed to resume upload:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = async () => {
        setLoading(true);
        try {
            const res = await api.post(`/api/uploads/${upload.id}/cancel`);
            // Merge response with existing upload to preserve relationships like contribution
            const updatedUpload = {...upload, ...res.data};
            setUploadStatus(updatedUpload);
            uploadsDispatch({type: 'setUploadState', uploadState: 'cancelled'});
            onUpdate && onUpdate(updatedUpload);
        } catch (error) {
            console.error('Failed to cancel upload:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRetry = async () => {
        setLoading(true);
        try {
            const res = await api.post(`/api/uploads/${upload.id}/retry_errored`);
            // Merge response with existing upload to preserve relationships like contribution
            const updatedUpload = {...upload, ...res.data};
            setUploadStatus(updatedUpload);
            uploadsDispatch({type: 'setUploadState', uploadState: updatedUpload.upload_state});
            onUpdate && onUpdate(updatedUpload);
            if(updatedUpload.upload_state !== 'cancelled') {
                notification.success({
                    message: t('retry-started', 'Retry Started'),
                    description: t('retry-started-description', 'Errored files are being retried.'),
                    duration: 4.5,
                });
            }
        } catch (error) {
            console.error('Failed to retry upload:', error);
            notification.error({
                message: t('retry-failed', 'Retry Failed'),
                description: t('retry-failed-description', 'Failed to retry errored files. Please try again.'),
            });
        } finally {
            setLoading(false);
        }
    };

    if (!uploadStatus) return null;

    const {
        total_assets = 0,
        pending_count = 0,
        uploading_count = 0,
        processing_count = 0,
        processed_count = 0,
        errored_count = 0,
        stalled_count = 0,
        upload_state = 'active'
    } = uploadStatus;

    const totalProgress = total_assets > 0
        ? ((processed_count + processing_count) / total_assets * 100).toFixed(1)
        : 0;

    const getStatusBadge = () => {
        // Check for error/stall states first
        if (upload_state === 'paused') {
            return <Tag icon={<PauseOutlined/>} color="warning">{t('paused', 'Paused')}</Tag>;
        }
        if (upload_state === 'stalled' || stalled_count > 0) {
            return <Tag icon={<ExclamationCircleOutlined/>} color="error">{t('stalled', 'Stalled')}</Tag>;
        }
        if (upload_state === 'cancelled') {
            return <Tag icon={<CloseCircleOutlined/>} color="default">{t('cancelled', 'Cancelled')}</Tag>;
        }
        if (upload_state === 'completed') {
            return <Tag icon={<CheckCircleOutlined/>} color="success">{t('completed', 'Completed')}</Tag>;
        }

        // Dynamic badge based on current activity
        if (uploading_count > 0) {
            return <Tag icon={<CloudUploadOutlined/>} color="processing">{t('uploading', 'Uploading')}</Tag>;
        }
        if (processing_count > 0) {
            return <Tag icon={<ClockCircleOutlined/>} color="processing">{t('processing', 'Processing')}</Tag>;
        }
        if (pending_count > 0) {
            return <Tag icon={<ClockCircleOutlined/>} color="default">{t('waiting', 'Waiting')}</Tag>;
        }
        if (processed_count === total_assets && total_assets > 0) {
            return <Tag icon={<CheckCircleOutlined/>} color="success">{t('complete', 'Complete')}</Tag>;
        }

        // Default
        return <Tag icon={<CloudUploadOutlined/>} color="processing">{t('active', 'Active')}</Tag>;
    };

    const getAlertMessage = () => {
        if (upload_state === 'stalled' || stalled_count > 0) {
            return {
                type: 'error',
                message: t('upload-stalled', 'Upload has stalled. Please check your connection and resume.'),
                icon: <ExclamationCircleOutlined/>
            };
        }
        if (errored_count > 0) {
            return {
                type: 'error',
                message: t('upload-has-errors', '{{count}} files failed to process. Review errors below.', {count: errored_count}),
                icon: <WarningOutlined/>
            };
        }
        if (upload_state === 'paused') {
            return {
                type: 'warning',
                message: t('upload-paused', 'Upload paused. Resume when ready.'),
                icon: <PauseOutlined/>
            };
        }
        return null;
    };

    const alertInfo = getAlertMessage();

    const statusData = [
        {key: 'total', label: t('selected-files', 'Selected Files').toProperCase(), value: total_assets},
        {key: 'pending', label: t('pending-uploads', 'Pending Uploads').toProperCase(), value: pending_count},
        {key: 'processing', label: t('processing', 'Processing').toProperCase(), value: processing_count},
        {key: 'complete', label: t('complete', 'Complete').toProperCase(), value: processed_count},
        {key: 'stalled', label: t('stalled', 'Stalled').toProperCase(), value: stalled_count, error: true},
        {key: 'errored', label: t('errored', 'Errored').toProperCase(), value: errored_count, error: true},
    ];

    return (
        <>
        <Collapse
            defaultActiveKey={['upload-progress']}
            style={{marginBottom: 8}}
            size="small"
            onChange={() => {
                // Reinitialize grid after collapse animation completes
                setTimeout(() => {
                    assetGroupDispatch({type: 'reinitializeGrid'});
                }, 300); // Wait for collapse animation to complete
            }}
        >
            <Collapse.Panel
                header={
                    <Space size="small">
                        {t('upload-progress', 'Upload Progress')}
                        {getStatusBadge()}
                        {upload_state === 'active' && (pending_count > 0 || uploading_count > 0) && (
                            <Tag icon={<CloudUploadOutlined/>} color="processing">
                                {t('upload-active-notice', 'Upload is active. Do not navigate away from the page.')}
                            </Tag>
                        )}
                    </Space>
                }
                key="upload-progress"
            >
            {alertInfo && (
                <Alert
                    type={alertInfo.type}
                    message={alertInfo.message}
                    icon={alertInfo.icon}
                    showIcon
                    closable={false}
                    style={{marginBottom: 6, padding: '6px 12px', fontSize: '12px'}}
                />
            )}

            <Progress
                percent={totalProgress}
                status={upload_state === 'stalled' || errored_count > 0 ? 'exception' : 'active'}
                strokeColor={upload_state === 'paused' ? '#faad14' : undefined}
                size="small"
            />

            {showStatusFilter && (
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    style={{marginTop: 8}}
                    size="small"
                >
                    <TabPane
                        tab={`${t('all', 'All')} (${total_assets})`}
                        key="all"
                    />
                    <TabPane
                        tab={`${t('submitted', 'Submitted')} (${processed_count})`}
                        key="submitted"
                    />
                    <TabPane
                        tab={`${t('un-submitted', 'Un-submitted')} (${pending_count + uploading_count + processing_count})`}
                        key="unsubmitted"
                    />
                    <TabPane
                        tab={
                            <span style={{color: stalled_count > 0 ? '#ff4d4f' : undefined}}>
                                {t('stalled', 'Stalled')} ({stalled_count})
                            </span>
                        }
                        key="stalled"
                    />
                    <TabPane
                        tab={
                            <span style={{color: errored_count > 0 ? '#ff4d4f' : undefined}}>
                                {t('errored', 'Errored')} ({errored_count})
                            </span>
                        }
                        key="errored"
                    />
                </Tabs>
            )}

            <Row gutter={[8, 6]} style={{marginTop: 8, marginBottom: 0}}>
                {statusData.map(item => (
                    <Col xs={24} sm={12} lg={8} key={item.key}>
                        <div style={{padding: '2px 0'}}>
                            <div style={{fontSize: '11px', color: '#8c8c8c', marginBottom: '1px'}}>
                                {item.label}
                            </div>
                            <div style={{
                                fontSize: '18px',
                                fontWeight: 600,
                                color: item.error && item.value > 0 ? '#ff4d4f' : undefined
                            }}>
                                {item.value}
                            </div>
                        </div>
                    </Col>
                ))}
            </Row>

            {(upload_state === 'active' && (pending_count > 0 || uploading_count > 0 || processing_count > 0)) ||
             (upload_state === 'paused' || upload_state === 'stalled') ||
             (errored_count > 0 && upload_state !== 'cancelled') ? (
                <div style={{marginTop: 8, display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                    {upload_state === 'active' && (pending_count > 0 || uploading_count > 0 || processing_count > 0) && (
                        <Button
                            icon={<PauseOutlined/>}
                            onClick={handlePause}
                            loading={loading}
                            size="small"
                        >
                            {t('pause', 'Pause')}
                        </Button>
                    )}

                    {(upload_state === 'paused' || upload_state === 'stalled') && (
                        <Button
                            type="primary"
                            icon={<PlayCircleOutlined/>}
                            onClick={handleResume}
                            loading={loading}
                            size="small"
                        >
                            {t('resume', 'Resume')}
                        </Button>
                    )}

                    {errored_count > 0 && upload_state !== 'cancelled' && (
                        <Button
                            type="default"
                            icon={<ReloadOutlined/>}
                            onClick={handleRetry}
                            loading={loading}
                            size="small"
                        >
                            {t('retry-errored', 'Retry Failed Files')}
                        </Button>
                    )}

                    {upload_state !== 'completed' && upload_state !== 'cancelled' && (pending_count > 0 || uploading_count > 0 || processing_count > 0) && (
                        <Popconfirm
                            title={t('confirm-cancel-upload', 'Cancel this upload? Already uploaded files will be kept.')}
                            onConfirm={handleCancel}
                            okText={t('yes', 'Yes')}
                            cancelText={t('no', 'No')}
                        >
                            <Button
                                danger
                                icon={<CloseCircleOutlined/>}
                                loading={loading}
                                size="small"
                            >
                                {t('cancel', 'Cancel')}
                            </Button>
                        </Popconfirm>
                    )}
                </div>
            ) : null}
            </Collapse.Panel>
        </Collapse>
        </>
    );
};
